import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { GetAllCustomersFiltered_getAllCustomersFiltered as Customer } from '../../../types/schemaTypes';
import { CustomButton } from '../../_common';
import SearchableDropdown from '../../_common/SearchableDropdown/SearchableDropdown';
import { Textfield } from '../../_common/Textfield/Textfield';
import { ModalForm } from '../interface';
import './AddEditCustomerModal.scss';

interface Option {
    id: number | string;
    name: string | number;
}

interface AddEditCustomerModalProps {
    modalForm: ModalForm;
    options: Option[];
    selectedCustomer?: Customer;
    onClickHide: () => void;
    onClickSave: (
        id: number,
        customerName: string,
        description: string,
        enabled: boolean,
        extSystemIdentifier: string,
        industryClassificationCode: string,
        customerParentId: number,
    ) => void;
    show: boolean;
    errorMessage?: string;
}

const AddEditCustomerModal = ({
    show,
    options,
    onClickHide,
    onClickSave,
    modalForm,
    selectedCustomer,
    errorMessage,
}: AddEditCustomerModalProps): React.ReactElement => {
    const [selectedId, setSelectedId] = useState(0);
    const [selectedCustomerName, setSelectedCustomerName] = useState('');
    const [selectedDesc, setSelectedDesc] = useState('');
    const [selectedClassCode, setSelectedClassCode] = useState('');
    const [selectedExtSysIdentifier, setSelectedExtSysIdentifier] = useState('');
    const [selectedCustParentId, setSelectedCustParentId] = useState(0);
    const [selectedEnabled, setSelectedEnabled] = useState(false);
    const [formErrors, setFormErrrors]: any = React.useState({});
    const isEditForm = modalForm === ModalForm.EDIT_CUSTOMER;

    useEffect(() => {
        if (selectedCustomer) {
            setSelectedId(selectedCustomer.id ? selectedCustomer.id : 0);
            setSelectedCustomerName(selectedCustomer.customerName ? selectedCustomer.customerName : ' ');
            setSelectedDesc(selectedCustomer.description ? selectedCustomer.description : ' ');
            setSelectedClassCode(
                selectedCustomer.industryClassificationCode ? selectedCustomer.industryClassificationCode : ' ',
            );
            setSelectedExtSysIdentifier(
                selectedCustomer.extSystemIdentifier ? selectedCustomer.extSystemIdentifier : ' ',
            );
            setSelectedEnabled(selectedCustomer.enabled ? selectedCustomer.enabled : false);
            setSelectedCustParentId(selectedCustomer.customerParentId ? selectedCustomer.customerParentId : 0);
        }
        setFormErrrors({});
    }, [selectedCustomer]);

    const clearState = (): void => {
        setSelectedId(0);
        setSelectedCustomerName('');
        setSelectedDesc('');
        setSelectedClassCode('');
        setSelectedExtSysIdentifier('');
        setSelectedEnabled(true);
        setSelectedCustParentId(0);
        setFormErrrors({});
    };
    const validateForm = () => {
        const errors = {};

        if (!selectedCustomerName || (selectedCustomer !== undefined && !selectedCustomer.customerName)) {
            errors['customerName'] = 'Customer Name is required';
        }
        if (!selectedCustParentId || (selectedCustomer !== undefined && !selectedCustomer.customerParentId)) {
            errors['custParent'] = 'Customer Parent is required';
        }
        if (!selectedDesc || (selectedCustomer !== undefined && !selectedCustomer.description)) {
            errors['description'] = 'Description is required';
        }
        return {
            isValid: Object.keys(errors).length === 0,
            errors,
        };
    };
    const onSave = (): void => {
        onClickSave(
            selectedId,
            selectedCustomerName,
            selectedDesc,
            selectedEnabled,
            selectedExtSysIdentifier,
            selectedClassCode,
            selectedCustParentId,
        );
        clearState();
    };
    const handleSaveChanges = () => {
        setFormErrrors({});
        const { isValid, errors } = validateForm();

        if (!isValid) {
            setFormErrrors(errors);
            return;
        }
        onSave();
    };
    const onCancel = (): void => {
        clearState();
        onClickHide();
    };

    const changeName = (event: any): void => {
        setSelectedCustomerName(event.target.value);
    };
    const changeDesc = (event: any): void => {
        setSelectedDesc(event.target.value);
    };
    const changeJDE = (event: any): void => {
        setSelectedExtSysIdentifier(event.target.value);
    };
    const changeClassCode = (event: any): void => {
        setSelectedClassCode(event.target.value);
    };
    const changeStatus = (event: any): void => {
        const boolValue = JSON.parse(event.target.value);

        setSelectedEnabled(boolValue);
    };
    const getCustomerParentName = (): string | number | null => {
        const id =
            selectedCustomer && selectedCustomer.customerParentId ? selectedCustomer.customerParentId.toString() : '';

        if (id == '') return null;

        const numId = parseInt(id);
        const filteredCustomerParent = options.filter((item: Option) => item.id === numId);
        const customerParent = filteredCustomerParent[0].name;

        return customerParent;
    };

    const changeCustomerParent = (value: any): void => {
        const id = parseInt(value);
        setSelectedCustParentId(id);
    };

    return (
        <Modal show={show} onHide={onClickHide} size="lg">
            <Modal.Header className="AddEditCustomerModal-Header">
                <Modal.Title className="AddEditCustomerModal-Header-Container">
                    <button className="material-icons AddEditCustomerModal-X-Button" onClick={onClickHide}>
                        close
                    </button>
                    <div className="AddEditCustomerModal-Title">
                        {isEditForm ? 'Edit Customer' : 'Create New Customer'}
                    </div>
                    <div className="AddEditCustomerModal-ErrorMessage">{errorMessage}</div>
                </Modal.Title>
            </Modal.Header>
            <div className="AddEditCustomerModal-Upper-TextField-Container">
                <div className="AddEditCustomerModal-CmpName">
                    <Textfield
                        label="CustomerName"
                        value={selectedCustomer !== undefined ? selectedCustomer.customerName : ''}
                        onChange={(event): void => changeName(event)}
                        onBlur={() => {}}
                        error={formErrors.customerName}
                        helperText={formErrors.customerName}
                    />
                </div>
                <div className="AddEditCustomerModal-DropDown">
                    <SearchableDropdown
                        dropdownOptions={options}
                        id="customerParent"
                        label="CustomerParent"
                        onSelect={(eventKey: string | null) => {
                            changeCustomerParent(eventKey);
                        }}
                        value={getCustomerParentName()}
                        errorMessage={formErrors.custParent}
                        hasError={formErrors.custParent != undefined}
                    />
                </div>
            </div>
            <div className="AddEditCustomerModal-Upper-TextField-Container">
                <div className="AddEditCustomerModal-CmpName">
                    <Textfield
                        label="Description"
                        value={selectedCustomer !== undefined ? selectedCustomer?.description : ''}
                        onChange={(event): void => changeDesc(event)}
                        onBlur={() => {}}
                        error={formErrors.description}
                        helperText={formErrors.description}
                        name="description"
                    />
                </div>
                <div className="AddEditCustomerModal-CmpName">
                    <Textfield
                        label="JDE ID#"
                        value={selectedCustomer !== undefined ? selectedCustomer.extSystemIdentifier : ''}
                        onChange={(event): void => changeJDE(event)}
                        onBlur={() => {}}
                    />
                </div>
                <div className="AddEditCustomerModal-CmpName">
                    <Textfield
                        label="Industry Classification Code"
                        value={selectedCustomer !== undefined ? selectedCustomer.industryClassificationCode : ''}
                        onChange={(event): void => changeClassCode(event)}
                        onBlur={() => {}}
                    />
                </div>
            </div>
            <div className="AddEditUserModal-HasAccess-Container">
                <div className="AddEditUserModal-HasAccess-Title">Is Customer Active?</div>
                <div className="AddEditUserModal-HasAccess-Radio-Container">
                    <div className="AddEditUserModal-Radio">
                        <input
                            className="AddEditUserModal-Radio-Input"
                            type="radio"
                            value="true"
                            checked={selectedEnabled}
                            onChange={(event): void => changeStatus(event)}
                        />
                        Yes
                    </div>
                    <div>
                        <input
                            className="AddEditUserModal-Radio-Input"
                            type="radio"
                            value="false"
                            checked={!selectedEnabled}
                            onChange={(event): void => changeStatus(event)}
                        />
                        No
                    </div>
                </div>
            </div>
            <Modal.Footer className="AddEditCustomerModal-Footer">
                <CustomButton title="Cancel" isBordered onClick={onCancel}></CustomButton>
                <CustomButton title="Save" onClick={handleSaveChanges}></CustomButton>
            </Modal.Footer>
        </Modal>
    );
};

export default AddEditCustomerModal;

